import React, { CSSProperties, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useOnboardingV3, { OnboardingStepV3 } from 'Hooks/useOnboardingV3';
import useScreen from 'Hooks/useScreen';
import Lottie from 'react-lottie';
import GetStartedLottie from 'assets/images/lotties/GetStarted.json';
import { ReactComponent as SpotifyIcon } from 'assets/images/platforms/spotify.svg';
import { ReactComponent as InstagramIcon } from 'assets/images/platforms/instagram.svg';
import { ReactComponent as TikTokIcon } from 'assets/images/platforms/tiktok.svg';
import InputSymphony from 'elements/InputSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import PageSymphony from 'elements/PageSymphony';
import CardSymphony from 'elements/CardSymphony';
import Icon from 'elements/Icon';
import SliderSymphony from 'elements/SliderSymphony';
import SocialConnectModal from 'components/shareable/SocialConnectModal';
import { SocialConnectType } from 'modules/Const';
import { styx } from 'modules/Utils';
import Colors from 'modules/Colors';
import Sty from 'modules/Sty';
import { SelectOptionItem } from 'elements/InputSymphony';

const AddBrandV3 = () => {
    const history = useHistory();
    const { mobileView, mStyle, cssCalc } = useScreen();
    
    const {
        brandName,
        setBrandName,
        selectedConnections,
        updateSingleConnection,
        hearAboutUs,
        setHearAboutUs,
        monthlyMarketingSpend,
        setMonthlyMarketingSpend,
        teamSize,
        setTeamSize,
        currentStep,
        setCurrentStep,
        isLoading,
        error,
        validateStep,
        goToNextStep,
        goToPreviousStep,
        submitOnboarding,
        resetOnboardingState,
    } = useOnboardingV3();

    // Local state for modal management
    const [isSocialConnectModalOpen, setIsSocialConnectModalOpen] = useState(false);
    const [socialConnectTypeToOpen, setSocialConnectTypeToOpen] = useState<SocialConnectType | null>(null);

    useEffect(() => {
        setCurrentStep(OnboardingStepV3.BRAND_NAME);
        return () => {
            resetOnboardingState();
        };
    }, [setCurrentStep]);

    useEffect(() => {
        if (currentStep === OnboardingStepV3.COMPLETED && !isLoading) {
            history.push('/dashboard');
        }
    }, [currentStep, isLoading, history]);

    const handlePlatformConnect = (platform: SocialConnectType) => {
        setSocialConnectTypeToOpen(platform);
        setIsSocialConnectModalOpen(true);
    };

    const onPlatformConnected = (connectionData: any) => {
        if (socialConnectTypeToOpen) {
            switch (socialConnectTypeToOpen) {
                case SocialConnectType.SPOTIFY:
                    updateSingleConnection('spotify', connectionData);
                    break;
                case SocialConnectType.INSTAGRAM:
                    updateSingleConnection('instagram', { url: connectionData.url });
                    break;
                case SocialConnectType.TIKTOK:
                    updateSingleConnection('tiktok', { url: connectionData.url });
                    break;
                case SocialConnectType.NEWSLETTER:
                    updateSingleConnection('newsletter', { url: connectionData.url });
                    break;
                case SocialConnectType.WEBSITE:
                    updateSingleConnection('website', { url: connectionData.url });
                    break;
                case SocialConnectType.SHOPIFY:
                    updateSingleConnection('shopify', { url: connectionData.url });
                    break;
                case SocialConnectType.TWITTER:
                    updateSingleConnection('twitter', { url: connectionData.url });
                    break;

                default:
                    break;
            }
        }
        setIsSocialConnectModalOpen(false);
    };

    const closeSocialConnectModal = () => {
        setIsSocialConnectModalOpen(false);
        setSocialConnectTypeToOpen(null);
    };

    const getStepProgress = () => {
        const steps = [OnboardingStepV3.BRAND_NAME, OnboardingStepV3.PLATFORM_CONNECTIONS, OnboardingStepV3.QUALIFYING_QUESTIONS];
        const currentIndex = steps.indexOf(currentStep);
        return ((currentIndex + 1) / steps.length) * 100;
    };

    const getStepNumber = () => {
        const steps = [OnboardingStepV3.BRAND_NAME, OnboardingStepV3.PLATFORM_CONNECTIONS, OnboardingStepV3.QUALIFYING_QUESTIONS];
        return steps.indexOf(currentStep) + 1;
    };

    const getStepTitle = () => {
        const steps = [OnboardingStepV3.BRAND_NAME, OnboardingStepV3.PLATFORM_CONNECTIONS, OnboardingStepV3.QUALIFYING_QUESTIONS];
        const currentIndex = steps.indexOf(currentStep);
        const stepNumber = currentIndex + 1;
        
        switch (currentStep) {
            case OnboardingStepV3.BRAND_NAME:
                return `Step ${stepNumber} of 3: Brand Information`;
            case OnboardingStepV3.PLATFORM_CONNECTIONS:
                return `Step ${stepNumber} of 3: Platforms`;
            case OnboardingStepV3.QUALIFYING_QUESTIONS:
                return `Step ${stepNumber} of 3: About You`;
            default:
                return '';
        }
    };

    const handleSkipPlatforms = () => {
        goToNextStep();
    };

    const renderBackButton = () => {
        if (currentStep === OnboardingStepV3.BRAND_NAME) return null;

        return (
            <Icon
                className="backButtonBrandOnboarding"
                style={sty.backButton}
                onClick={goToPreviousStep}
                icon="arrowLeft"
                isCircle
            />
        );
    };

    const renderStepIndicator = () => {
        return (
            <div className="stepIndicatorContainer" style={sty.stepIndicatorContainer}>
                <div className="stepTitle" style={mStyle(sty, 'simpleStepTitle')}>
                    {getStepTitle()}
                </div>
            </div>
        );
    };

    const renderLeftColumn = () => {
        return (
            <div
                className="leftColumnBrandOnboarding"
                style={styx(
                    mStyle(sty, 'leftColumnBrandOnboarding'),
                    { height: cssCalc() }
                )}
            >
                <div
                    className="leftColumnBrandOnboarding_inner"
                    style={mStyle(sty, 'leftColumnBrandOnboarding_inner')}
                >
                    {renderStepIndicator()}
                    <div
                        className="stepContentContainer"
                        style={mStyle(sty, 'stepContentContainer')}
                    >
                        {renderStepContent()}
                    </div>
                    {renderStickyButtons()}
                </div>
            </div>
        );
    };

    const renderRightColumn = () => {
        if (mobileView) return null;

        return (
            <div
                className="rightColumnBrandOnboarding"
                style={styx(
                    sty.rightColumnBrandOnboarding,
                    { height: cssCalc() }
                )}
            >
                <Lottie
                    style={{ marginRight: -18 }}
                    options={{
                        loop: true,
                        autoplay: true,
                        animationData: GetStartedLottie,
                    }}
                />
            </div>
        );
    };

    const renderBrandNameStep = () => {
        return (
            <>
                <CardSymphony
                    className="brandNameCard"
                    title="Welcome to SymphonyOS! Tell us a bit about yourself."
                    isNoPadding
                    style={sty.stepCard}
                />
                <InputSymphony
                    className="brandNameInput"
                    label="What's your name? (your brand's name, your artist name, etc)"
                    placeholder="e.g. My Artist Name, Cool Apparel Co."
                    value={brandName}
                    onChange={setBrandName}
                    onEnter={goToNextStep}
                    style={sty.brandNameInput}
                />
                {error && (
                    <div className="errorMessage" style={sty.errorMessage}>
                        {error}
                    </div>
                )}
                <ButtonSymphony
                    className="continueButton"
                    onClick={goToNextStep}
                    disabled={!validateStep(currentStep) || isLoading}
                    isLoading={isLoading}
                    size="large"
                    style={sty.continueButton}
                >
                    Continue
                </ButtonSymphony>
            </>
        );
    };

    const renderPlatformConnectionsStep = () => {
        const platforms = [
            {
                icon: <SpotifyIcon width={32} height={32} />,
                name: 'Spotify',
                description: 'Connect your artist profile',
                type: SocialConnectType.SPOTIFY,
                connected: !!selectedConnections.spotify,
            },
            {
                icon: <InstagramIcon width={32} height={32} />,
                name: 'Instagram',
                description: 'Link your Instagram account',
                type: SocialConnectType.INSTAGRAM,
                connected: !!selectedConnections.instagram,
            },
            {
                icon: <TikTokIcon width={32} height={32} />,
                name: 'TikTok',
                description: 'Connect your TikTok profile',
                type: SocialConnectType.TIKTOK,
                connected: !!selectedConnections.tiktok,
            },
            {
                icon: <Icon className="platformTwitterIcon" icon="twitter" size={32} />,
                name: 'Twitter/X',
                description: 'Connect your Twitter profile',
                type: SocialConnectType.TWITTER,
                connected: !!selectedConnections.twitter,
            },
            {
                icon: <Icon className="platformNewsletterIcon" icon="mail" size={32} />,
                name: 'Newsletter',
                description: 'Add your newsletter signup',
                type: SocialConnectType.NEWSLETTER,
                connected: !!selectedConnections.newsletter,
            },
            {
                icon: <Icon className="platformWebsiteIcon" icon="link" size={32} />,
                name: 'Website',
                description: 'Link your main website',
                type: SocialConnectType.WEBSITE,
                connected: !!selectedConnections.website,
            },
            {
                icon: <Icon className="platformShopifyIcon" icon="store" size={32} />,
                name: 'Shopify',
                description: 'Connect your online store',
                type: SocialConnectType.SHOPIFY,
                connected: !!selectedConnections.shopify,
            },
        ];

        return (
            <>
                <CardSymphony
                    className="platformConnectionsCard"
                    title="Your AI gets more powerful, the more it knows you. Connect as many accounts as you'd like to get started. Or you can skip."
                    isNoPadding
                    style={sty.stepCard}
                />

                <div className="platformsGrid" style={mStyle(sty, 'platformsGrid')}>
                    {platforms.map((platform) => (
                        <div
                            key={platform.type}
                            className="platformCard"
                            style={styx(
                                sty.platformCard,
                                platform.connected && sty.platformCardConnected
                            )}
                            onClick={() => handlePlatformConnect(platform.type)}
                        >
                            <div className="platformIcon" style={sty.platformIcon}>
                                {platform.icon}
                            </div>
                            <div className="platformInfo" style={sty.platformInfo}>
                                <h3 className="platformName" style={sty.platformName}>
                                    {platform.name}
                                </h3>
                                <p className="platformDescription" style={sty.platformDescription}>
                                    {platform.description}
                                </p>
                            </div>
                            <div className="platformAction" style={sty.platformAction}>
                                {platform.connected ? (
                                    <Icon
                                        className="connectedIcon"
                                        icon="check"
                                        size={20}
                                        style={sty.connectedIcon}
                                    />
                                ) : (
                                    <Icon
                                        className="chevronIcon"
                                        icon="chevronRight"
                                        size={20}
                                        style={sty.chevronIcon}
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {!mobileView && (
                    <div className="buttonsContainer" style={mStyle(sty, 'buttonsContainer')}>
                        <ButtonSymphony
                            className="skipButton"
                            onClick={handleSkipPlatforms}
                            variant="text"
                            style={sty.skipButton}
                        >
                            Skip for now
                        </ButtonSymphony>
                        <ButtonSymphony
                            className="continueButton"
                            onClick={goToNextStep}
                            size="large"
                            style={sty.continueButton}
                        >
                            Continue
                        </ButtonSymphony>
                    </div>
                )}
            </>
        );
    };

    const renderQualifyingQuestionsStep = () => {
        const budgetOptions: SelectOptionItem[] = [
            { value: 'None', title: 'None', description: '$0' },
            { value: '$10-100', title: '$10-100', description: 'Getting started' },
            { value: '$100-500', title: '$100-500', description: 'Growing' },
            { value: '$500-1K', title: '$500-1K', description: 'Scaling' },
            { value: '$1K+', title: '$1K+', description: 'Established' },
        ];

        const teamSizeOptions: SelectOptionItem[] = [
            { value: 'just_me', title: "It's just me!" },
            { value: 'small_team', title: '2-5 people' },
            { value: 'medium_team', title: '5-10 people' },
            { value: 'large_team', title: '10+ people' },
        ];

        const hearAboutUsOptions: SelectOptionItem[] = [
            { value: 'google', title: 'Google Search' },
            { value: 'social_media', title: 'Social Media' },
            { value: 'friend_referral', title: 'Friend or Colleague' },
            { value: 'youtube', title: 'YouTube' },
            { value: 'podcast', title: 'Podcast' },
            { value: 'blog', title: 'Blog or Article' },
            { value: 'event', title: 'Event or Conference' },
            { value: 'other', title: 'Other' },
        ];

        const handleTeamSizeChange = (value: string) => {
            setTeamSize(value as typeof teamSize);
        };

        return (
            <>
                <CardSymphony
                    className="qualifyingQuestionsCard"
                    title="Last step to help us setup your profile"
                    isNoPadding
                    style={sty.stepCard}
                />

                <div className="questionsContainer" style={sty.questionsContainer}>
                    <div className="budgetDropdownContainer" style={sty.budgetDropdownContainer}>
                        <InputSymphony
                            className="budgetSelect"
                            label="How much do you typically spend a month on marketing?"
                            placeholder="Select your monthly marketing spend"
                            value={monthlyMarketingSpend || ''}
                            onChange={setMonthlyMarketingSpend}
                            isSelectDropdown
                            options={budgetOptions}
                            style={sty.questionInput}
                        />
                    </div>

                    <div className="teamSizeContainer" style={sty.teamSizeContainer}>
                        <InputSymphony
                            className="teamSizeSelect"
                            label="How big is your team?"
                            placeholder="Select team size"
                            value={teamSize || ''}
                            onChange={handleTeamSizeChange}
                            isSelectDropdown
                            options={teamSizeOptions}
                            style={sty.questionInput}
                        />
                    </div>

                    <div className="hearAboutUsContainer" style={sty.hearAboutUsContainer}>
                        <InputSymphony
                            className="hearAboutUsSelect"
                            label="How did you hear about us?"
                            placeholder="Select an option"
                            value={hearAboutUs || ''}
                            onChange={setHearAboutUs}
                            isSelectDropdown
                            options={hearAboutUsOptions}
                            style={sty.questionInput}
                        />
                    </div>
                </div>

                {error && (
                    <div className="errorMessage" style={sty.errorMessage}>
                        {error}
                    </div>
                )}

                {!mobileView && (
                    <ButtonSymphony
                        className="finishButton"
                        onClick={submitOnboarding}
                        disabled={!validateStep(currentStep) || isLoading}
                        isLoading={isLoading}
                        size="large"
                        style={sty.continueButton}
                    >
                        Let's get going
                    </ButtonSymphony>
                )}
            </>
        );
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case OnboardingStepV3.BRAND_NAME:
                return renderBrandNameStep();
            case OnboardingStepV3.PLATFORM_CONNECTIONS:
                return renderPlatformConnectionsStep();
            case OnboardingStepV3.QUALIFYING_QUESTIONS:
                return renderQualifyingQuestionsStep();
            default:
                return null;
        }
    };

    const renderStickyButtons = () => {
        if (!mobileView) {
            return null;
        }

        if (currentStep === OnboardingStepV3.PLATFORM_CONNECTIONS) {
            return (
                <div className="stickyButtonsContainer" style={sty.stickyButtonsContainer}>
                    <ButtonSymphony
                        className="skipButton"
                        onClick={handleSkipPlatforms}
                        variant="text"
                        style={sty.skipButton}
                    >
                        Skip for now
                    </ButtonSymphony>
                    <ButtonSymphony
                        className="continueButton"
                        onClick={goToNextStep}
                        size="large"
                        style={sty.continueButton}
                    >
                        Continue
                    </ButtonSymphony>
                </div>
            );
        }

        if (currentStep === OnboardingStepV3.QUALIFYING_QUESTIONS) {
            return (
                <div className="stickyButtonsContainer" style={sty.stickyButtonsContainer}>
                    <ButtonSymphony
                        className="finishButton"
                        onClick={submitOnboarding}
                        disabled={!validateStep(currentStep) || isLoading}
                        isLoading={isLoading}
                        size="large"
                        style={sty.continueButton}
                    >
                        Let's get going
                    </ButtonSymphony>
                </div>
            );
        }

        return null;
    };

    return (
        <PageSymphony
            className="addBrandV3Component"
            style={styx(
                sty.addBrandV3Component,
                { height: cssCalc() }
            )}
            maxWidth={1200}
            isNoPadding
        >
            {renderLeftColumn()}
            {renderRightColumn()}

            {/* Modals */}
            {socialConnectTypeToOpen && (
                <SocialConnectModal
                    className="brandOnboardingSocialConnectModal"
                    isOpen={isSocialConnectModalOpen}
                    onClose={closeSocialConnectModal}
                    socialConnectType={socialConnectTypeToOpen}
                    onConnect={onPlatformConnected}
                    isConnectToBrand={false}
                />
            )}
        </PageSymphony>
    );
};

const sty: Record<string, CSSProperties> = {
    addBrandV3Component: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        padding: 0,
        backgroundColor: 'white',
    },
    leftColumnBrandOnboarding: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        width: '50%',
        padding: 24,
        gap: 16,
        overflowY: 'scroll',
    },
    leftColumnBrandOnboarding_mobile: {
        width: '100%',
        padding: 16,
        justifyContent: 'flex-start',
        overflowY: 'auto',
    },
    leftColumnBrandOnboarding_inner: {
        maxWidth: 680,
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    leftColumnBrandOnboarding_inner_mobile: {
        maxWidth: 680,
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    rightColumnBrandOnboarding: {
        width: '50%',
        background: Colors.gradientTeal,
        paddingLeft: 40,
        paddingTop: 24,
        paddingBottom: 24,
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 24,
        cursor: 'pointer',
    },
    stepIndicatorContainer: {
        marginBottom: 32,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    simpleStepTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: Colors.purple,
        textAlign: 'center',
    },
    simpleStepTitle_mobile: {
        fontSize: 14,
        fontWeight: 600,
        color: Colors.purple,
        textAlign: 'center',
    },
    stepsHeader: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 16,
    },
    stepHeader: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginRight: 16,
    },
    stepNumber: {
        width: 24,
        height: 24,
        borderRadius: '50%',
        backgroundColor: Colors.greyLight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 4,
    },
    stepNumberActive: {
        backgroundColor: Colors.purple,
    },
    stepNumberCompleted: {
        backgroundColor: Colors.green,
    },
    stepTitle: {
        margin: 0,
        fontSize: 14,
        color: Colors.greyDark,
    },
    stepTitleActive: {
        color: Colors.purple,
    },
    stepConnector: {
        width: 40,
        height: 2,
        backgroundColor: Colors.greyLight,
    },
    stepConnectorCompleted: {
        backgroundColor: Colors.green,
    },
    stepCard: {
        marginBottom: 32,
    },
    brandNameInput: {
        marginBottom: 32,
        fontSize: 16,
    },
    continueButton: {
        marginLeft: 'auto',
        marginRight: 'auto',
        width: 300,
    },
    errorMessage: {
        color: Colors.red,
        fontSize: 14,
        marginBottom: 16,
        textAlign: 'center',
    },

    platformsGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 16,
        marginBottom: 32,
        maxHeight: 400,
        overflowY: 'auto',
        padding: 8,
    },
    platformsGrid_mobile: {
        gridTemplateColumns: '1fr',
        gap: 12,
        maxHeight: 300,
    },
    platformCard: {
        display: 'flex',
        alignItems: 'center',
        padding: 16,
        border: `2px solid ${Colors.greyLight}`,
        borderRadius: 12,
        backgroundColor: 'white',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        position: 'relative',
        minHeight: 80,
    },
    platformCardConnected: {
        borderColor: Colors.green,
        backgroundColor: Colors.greenLight,
    },
    platformIcon: {
        marginRight: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 48,
        height: 48,
        borderRadius: 8,
        backgroundColor: Colors.greyLight,
    },
    platformInfo: {
        flex: 1,
    },
    platformName: {
        margin: 0,
        marginBottom: 2,
        fontSize: 16,
        fontWeight: 600,
        color: Colors.textColor,
    },
    platformDescription: {
        margin: 0,
        fontSize: 13,
        color: Colors.greyDark,
    },
    platformAction: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: 8,
    },
    connectedIcon: {
        color: Colors.green,
    },
    chevronIcon: {
        color: Colors.grey,
    },
    additionalPlatforms: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        marginBottom: 32,
    },
    additionalInput: {
        fontSize: 14,
    },
    buttonsContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
    },
    stepContentContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    stepContentContainer_mobile: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        paddingBottom: 16,
    },
    stickyButtonsContainer: {
        position: 'sticky',
        bottom: 0,
        backgroundColor: 'white',
        padding: '16px 0',
        marginTop: 'auto',
        borderTop: `1px solid ${Colors.greyLight}`,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
    },
    skipButton: {
        color: Colors.grey,
    },
    questionsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
        marginBottom: 32,
    },
    questionInput: {
        fontSize: 16,
    },
    budgetDropdownContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    teamSizeContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    hearAboutUsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
};

export default AddBrandV3;
